import type { ReactNode } from "react";
import { StyleSheet, type TextProps, View, type ViewProps } from "react-native";
import { ListingItem, type ListingItemProps } from "~/components/layout/ListingItem";
import { Title } from "~/components/layout/text";
import { Only } from "~/components/permission/Only";

import { Radius } from "~/constants/sizing";
import { useTheme } from "~/providers/ThemeProvider";

export type ListItem = {
    key?: string;
    hide?: boolean;
} & ListingItemProps;

export type ListingGroupProps = {
    title?: ReactNode;
    children?: ReactNode;
    titleProps?: Omit<TextProps, "children">;
    bodyProps?: Omit<ViewProps, "children">;
    items?: ListItem[];
    keyExtractor?: (item: ListingItemProps, index: number, items: ListItem[]) => string;
} & ViewProps;

export const ListingGroup = ({
    title,
    children,
    titleProps,
    bodyProps,
    items,
    keyExtractor,
    ...viewProps
}: ListingGroupProps) => {
    const { background } = useTheme();
    return (
        <View
            {...viewProps}
            style={[styles.container, viewProps.style]}
            accessibilityRole={viewProps.accessibilityRole ?? "menu"}
        >
            <Only when={title}>
                <Title {...titleProps} size="md" colour="secondary" accessibilityRole="header">
                    {title}
                </Title>
            </Only>
            <View {...bodyProps} style={[styles.body, background.primary, bodyProps?.style]}>
                {children ??
                    items
                        ?.filter(({ hide }) => !hide)
                        ?.map(({ key, ...props }, idx, array) => (
                            <ListingItem
                                key={key ?? keyExtractor?.(props as ListingItemProps, idx, array)}
                                {...props}
                                last={props.last ?? idx === array.length - 1}
                            />
                        ))}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        gap: 12,
        alignItems: "stretch",
    },
    body: {
        borderRadius: Radius.lg,
        overflow: "hidden",
        alignItems: "stretch",
    },
});
